"use client";

import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import { AttributeValue, AttributeValueState } from "@/app/(main)/products/[...slugs]/Providers/ProductPageProvider";

function OptionChip({
  value,
  selected,
  state,
  onChange,
}: {
  value: string;
  selected: boolean;
  state: AttributeValueState;
  onChange: (value: string) => void;
}) {
  const isDisabled = state === "disabled";
  const isIncompatible = state === "incompatible";
  const [tempUnavailable, setTempUnavailable] = useState(false);

  const handleClick = () => {
    if (isDisabled) {
      setTempUnavailable(true);
      window.setTimeout(() => setTempUnavailable(false), 1200);
      return;
    }
    onChange(value);
  };

  const label = isDisabled
    ? `${value} — ناموجود`
    : isIncompatible
      ? `${value} — با انتخاب فعلی موجود نیست؛ با کلیک ترکیب موجود انتخاب می‌شود`
      : value;

  return (
    <Box
      component="button"
      type="button"
      aria-pressed={selected}
      aria-label={label}
      title={isIncompatible ? "با انتخاب فعلی موجود نیست" : undefined}
      disabled={false}
      onClick={handleClick}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        flex: "0 0 auto",
        m: 0,
        minWidth: 72,
        height: { xs: 36, sm: 34 },
        px: 1.25,
        borderRadius: 1.25,
        borderStyle: "solid",
        borderWidth: selected ? 1.5 : 1,
        borderColor: selected ? "grey.900" : isDisabled ? "grey.200" : "grey.300",
        bgcolor: selected ? "grey.50" : "#fff",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.45 : isIncompatible && !selected ? 0.72 : 1,
        color: selected ? "grey.900" : isIncompatible ? "grey.600" : "grey.800",
        font: "inherit",
        transition: "border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease, color 0.15s ease",
        textDecoration: isDisabled ? "line-through" : "none",
        "&:hover": isDisabled
          ? undefined
          : {
              borderColor: "grey.900",
              opacity: 1,
            },
      }}
    >
      {selected ? <CheckIcon sx={{ fontSize: 16, color: "grey.900" }} /> : null}

      <Typography
        component="span"
        sx={{
          fontSize: "0.75rem",
          fontWeight: selected ? 700 : 500,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          color: "inherit",
        }}
      >
        {value}
      </Typography>

      {tempUnavailable ? (
        <Typography
          component="span"
          variant="caption"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "inherit",
            bgcolor: "grey.100",
            color: "grey.700",
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          ناموجود
        </Typography>
      ) : null}
    </Box>
  );
}

interface VariantAttributeSelectorProps {
  name: string;
  values: AttributeValue[];
  selected?: string;
  prompted?: boolean;
  onSelect: (value: string) => void;
}

function AttributeGuideIcon({ name }: { name: string }) {
  const normalized = name.replace(/\s/g, "");
  const sx = { fontSize: 18, color: "secondary.800" } as const;
  if (/رنگ|color|colour/i.test(normalized)) return <PaletteOutlinedIcon sx={sx} />;
  if (/سایز|اندازه|size/i.test(normalized)) return <StraightenOutlinedIcon sx={sx} />;
  return <DiamondOutlinedIcon sx={sx} />;
}

/**
 * Variant picker: "{name}: {selected}" plus a wrapping row of outlined chips.
 */
export function VariantAttributeSelector({ name, values, selected, prompted = false, onSelect }: VariantAttributeSelectorProps) {
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {selected ? (
        <Stack direction="row" alignItems="baseline" spacing={0.75} useFlexGap flexWrap="wrap">
          <Typography component="span" sx={{ fontSize: "0.875rem", color: "grey.600", lineHeight: 1.6 }}>
            {name}:
          </Typography>
          <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 700, color: "grey.900", lineHeight: 1.6 }}>
            {selected}
          </Typography>
        </Stack>
      ) : (
        <Box
          className={prompted ? "scroll-target-glow" : undefined}
          sx={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.7,
            borderRadius: 2,
            bgcolor: prompted ? "secondary.200" : "secondary.50",
            border: "1px solid",
            borderColor: prompted ? "secondary.main" : "secondary.300",
            boxShadow: prompted ? "0 0 0 3px #E7A66B40" : "none",
            color: "secondary.900",
          }}
        >
          <AttributeGuideIcon name={name} />
          <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 800, lineHeight: 1.5, color: "secondary.900" }}>
            اول {name} را انتخاب کنید
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: prompted ? "secondary.400" : "grey.100",
          pb: 1.25,
          minWidth: 0,
        }}
      >
        <Box
          role="listbox"
          aria-label={name}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            pb: 0.25,
          }}
        >
          {values.map((item) => (
            <OptionChip
              key={item.value}
              value={item.value}
              selected={item.value === selected}
              state={item.state}
              onChange={onSelect}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );
}
